// Scoped cache invalidation.
//
// Two places in this app need to refresh more than the query they are standing
// in: the global undo shortcut (UndoManager) and a contacts merge
// (pages/contacts.tsx). Both used to call `queryClient.invalidateQueries()`
// with no filter, which invalidates *every* query in the cache and refetches
// the active ones. `staleTime` is not consulted -- so every caching decision
// anywhere in the app held only until someone pressed Cmd+Z, and nothing in the
// code said so (ROADMAP CACHE-01).
//
// What a query key looks like here
// --------------------------------
// @refinedev/core 5.0.12 builds its data keys as
//     ["data", <dataProviderName>, <resource>, <action>, ...]
// (see `KeyBuilder` / `DataKeyBuilder` in @refinedev/core's dist). Resource
// names in the key are refine *identifiers*; App.tsx declares no `identifier`
// on any resource, so identifier === resource name === the string the
// dataProvider is called with.
//
// The hand-written useQuery in the app names its resource in the key too:
//     ["global-search", <resource>, <term>]   (GlobalSearchPalette.tsx)
//
// Both shapes are covered by "one of these names appears as a string element of
// the key", which is what `keyTouches` tests. Non-string elements are
// deliberately not searched: the tail of a refine key is a params object, and a
// resource name can appear inside a filter value of a query that is not about
// that resource.

import type { QueryClient } from "@tanstack/react-query";

export function keyTouches(key: readonly unknown[] | undefined, resources: readonly string[]): boolean {
  if (!key) return false;
  return key.some((seg) => typeof seg === "string" && resources.includes(seg));
}

// Invalidate every cached query whose key names one of `resources`, and nothing
// else. Queries that merely *derive* from those collections (a count of tasks
// shown on an organisation, say) are not reached and will hold until their own
// staleTime expires -- that is the deliberate trade for not throwing the whole
// cache away. If a view needs to follow a write in another collection, name
// that collection at the call site rather than widening this.
//
// An empty `resources` matches nothing rather than everything: that falls out
// of the predicate, so there is no early-return guard here. A guard was written
// first and a mutant that deleted it changed no test result, which is how it was
// found to be doing nothing.
export function invalidateResources(qc: QueryClient, resources: readonly string[]): Promise<void> {
  return qc.invalidateQueries({ predicate: (q) => keyTouches(q.queryKey, resources) });
}

// Collections a contacts merge writes. The server moves every child row from
// the loser to the survivor, tombstones the loser, repoints any staff login
// that pointed at it, and records the merge so it can be undone. The child list
// is CHILD_COLLECTIONS in
// directus/extensions/directus-extension-contacts/src/merge/fold.ts; keep this
// in step with it.
export const CONTACT_MERGE_RESOURCES = [
  "contacts",
  "contact_emails",
  "contact_phones",
  "contact_affiliations",
  "contact_notes",
  "contact_merge_log",
  "contact_dupe_dismissals",
  "directus_users",
] as const;
